import AppOptions from "../app/options";
import User from "./user";
import {fetchWithTimeout} from './fetch';
import VideoPlaybackOptions from './video_playback_options';
import Html from "./html";

class MuxStatistics {
    // Reused by later playbacks on the same page, e.g. next episode.
    private enabled: boolean | null = null;

    constructor(
        private html: Html,
        private user: User,
        private options: AppOptions,
        private appType: 'web' | 'tvapp'
    ) {}

    loadConfig(playbackOptions: VideoPlaybackOptions): Promise<void> {
        if (!this.options.playbackStatsUrl || !this.options.playbackStatsKey || playbackOptions.isTrailer) {
            return Promise.resolve();
        }

        if (this.enabled !== null) {
            return Promise.resolve();
        }

        return this.fetchConfig()
            .then(data => {
                this.enabled = data && !!data.enabled || false;
            })
            .catch(() => {
                // Stats must not break playback. enabled stays null, so loadScript() skips and the next playback retries.
            });
    }

    loadScript(): Promise<boolean> {
        if (!this.enabled) {
            return Promise.resolve(false);
        }

        return this.html.loadScript('/assets/mux/shakaplayer-mux_5.14.28.js')
            .catch(() => {
                // Stats must not break playback.
                return false;
            });
    }

    monitor(video: HTMLMediaElement, playbackOptions: VideoPlaybackOptions, player: any): void {
        if (!(window as any).initShakaPlayerMux || !this.enabled || playbackOptions.isTrailer) {
            return;
        }

        this.startMonitoring(video, playbackOptions, player);
    }

    private fetchConfig(): Promise<any> {
        const configUrl = `${this.options.playbackStatsUrl}/config/${this.appType}`;
        const options = {
            timeout: 500,
            headers: {
                'Device-Id': this.user?.deviceId || '',
                'Onl-Location': document.location.href,
            }
        };

        return fetchWithTimeout(configUrl, options)
            .then(r => r?.json());
    }

    private startMonitoring(video: HTMLMediaElement, playbackOptions: VideoPlaybackOptions, player: any): void {
        let payload = {};

        switch (playbackOptions.type) {
            case 'live_stream':
                payload = this.liveStreamPayload(video, playbackOptions);
                break;
            case 'epg':
                payload = this.epgPayload(video, playbackOptions);
                break;
            default:
                payload = this.videoPayload(video, playbackOptions);
                break;
        }

        const muxData = Object.assign(this.devicePayload(), payload);
        const debug = this.user.user?.id === 19;

        initShakaPlayerMux(player, {
            debug: debug,
            beaconCollectionDomain: this.options.playbackStatsUrl?.replace('https://', ''), // 'https://' prepends mux by itself
            data: muxData,
            minimumRebufferDuration: 1000, // Only track rebuffers > 1000ms (less than this is not visible to user)
            sustainedRebufferThreshold: 2500, // Mark rebuffers > 2.5s as "sustained" ( = annoying for user)
        });
    }

    private devicePayload(): any {
        return {
            env_key: this.options.playbackStatsKey,
            viewer_user_id: this.user.user?.id,
            viewer_auth: this.user.user?.token || '',
            player_name: this.appType === 'web' ? 'WEB' : 'TV',
            app_version: this.options.version,
            device_family: this.options.device.family,
            device_model: this.options.device.model,
        }
    }

    private liveStreamPayload(video: HTMLMediaElement, playbackOptions: VideoPlaybackOptions): any {
        return {
            video_id: playbackOptions.mediaId,
            video_title: playbackOptions.title,
            video_stream_type: 'LIVE_STREAM',
            video_timeline: 0,
            video_mime: 0
        }
    }

    private epgPayload(video: HTMLMediaElement, playbackOptions: VideoPlaybackOptions): any {
        return {
            video_id: 0,
            video_title: playbackOptions.title,
            video_stream_type: 'EPG',
            video_channel: playbackOptions.channel || '',
            video_timeline: playbackOptions.chunkStart || 0,
            video_mime: 0
        }
    }

    private videoPayload(video: HTMLMediaElement, playbackOptions: VideoPlaybackOptions): any {
        return {
            video_id: playbackOptions.mediaId,
            video_title: playbackOptions.title,
            video_duration: playbackOptions.length ? playbackOptions.length * 1000 : 0,
            video_stream_type: 'VOD',
            video_channel: '',
            video_timeline: 0,
            video_mime: 0,
        }
    }
}

export default MuxStatistics;